import type { 
  TimelinePoint, 
  TimelineData, 
  Milestone,
  CrossoverPoint,
  ScenarioInput 
} from '@/types/supremeIntelligenceTypes';
import { calculateMortgagePayment } from '@/components/calculator/supreme-intelligence/utils/mortgageUtils';

export const generateTimelineData = (
  scenario: ScenarioInput,
  appreciationRate: number = 3
): TimelineData => {
  const downPaymentAmount = scenario.homePrice * (scenario.downPaymentPercentage / 100);
  const loanAmount = scenario.homePrice - downPaymentAmount;
  const monthlyRate = scenario.interestRate / 100 / 12;
  const totalMonths = scenario.loanTerm * 12;
  
  const monthlyPI = calculateMortgagePayment(loanAmount, scenario.interestRate, scenario.loanTerm);
  const monthlyTax = (scenario.homePrice * scenario.propertyTaxRate / 100) / 12;
  const monthlyInsurance = scenario.homeInsurance / 12;
  const monthlyHOA = scenario.hoaFees;
  
  const points: TimelinePoint[] = [];
  const milestones: Milestone[] = [];
  
  let remainingBalance = loanAmount;
  let cumulativePrincipal = 0;
  let cumulativeInterest = 0;
  let pmiRemoved = false;
  
  for (let month = 1; month <= totalMonths; month++) {
    const year = Math.floor((month - 1) / 12) + 1;
    
    // Calculate home value with appreciation
    const homeValue = scenario.homePrice * Math.pow(1 + appreciationRate / 100, (month - 1) / 12);
    
    // Calculate monthly payment breakdown
    const interestPayment = remainingBalance * monthlyRate;
    const principalPayment = Math.min(monthlyPI - interestPayment, remainingBalance);
    
    remainingBalance = Math.max(0, remainingBalance - principalPayment);
    cumulativePrincipal += principalPayment;
    cumulativeInterest += interestPayment;
    
    const currentEquity = homeValue - remainingBalance;
    const equityPercentage = (currentEquity / homeValue) * 100;
    
    // Check for PMI removal at 20% equity
    if (!pmiRemoved && scenario.downPaymentPercentage < 20 && equityPercentage >= 20) {
      pmiRemoved = true;
      milestones.push({
        month,
        type: 'pmi-removed',
        description: 'PMI Removed (20% Equity Reached)',
        value: currentEquity
      });
    }
    
    // Halfway through principal
    if (month === Math.floor(totalMonths / 2)) {
      milestones.push({
        month,
        type: 'halfway-paid',
        description: 'Halfway Through Loan Term',
        value: cumulativePrincipal
      });
    }
    
    // 50% equity milestone
    if (equityPercentage >= 50 && (points.length === 0 || points[points.length - 1].equity / homeValue < 0.5)) {
      milestones.push({
        month,
        type: 'equity-50',
        description: '50% Equity Achieved',
        value: currentEquity
      });
    }
    
    // 80% equity milestone
    if (equityPercentage >= 80 && (points.length === 0 || points[points.length - 1].equity / homeValue < 0.8)) {
      milestones.push({
        month,
        type: 'equity-80',
        description: '80% Equity Achieved',
        value: currentEquity
      });
    }
    
    const totalPaid = cumulativePrincipal + cumulativeInterest;
    
    points.push({
      month,
      year,
      principalPaid: principalPayment,
      interestPaid: interestPayment,
      cumulativePrincipal,
      cumulativeInterest,
      remainingBalance,
      homeValue,
      equity: currentEquity,
      totalPaid
    });
  }
  
  // Loan payoff milestone
  milestones.push({
    month: totalMonths,
    type: 'loan-payoff',
    description: 'Loan Fully Paid Off',
    value: loanAmount
  });
  
  const finalPoint = points[points.length - 1];
  
  return {
    scenarioId: scenario.id,
    scenarioName: scenario.name,
    points,
    milestones,
    summary: {
      totalPrincipal: cumulativePrincipal,
      totalInterest: cumulativeInterest,
      finalEquity: finalPoint.equity,
      monthsUntilPMIRemoval: milestones.find(m => m.type === 'pmi-removed')?.month
    }
  };
};

export const findCrossoverPoints = (
  timelines: TimelineData[]
): CrossoverPoint[] => {
  if (timelines.length < 2) return [];
  
  const crossoverPoints: CrossoverPoint[] = [];
  
  // Compare each pair of scenarios
  for (let i = 0; i < timelines.length - 1; i++) {
    for (let j = i + 1; j < timelines.length; j++) {
      const timeline1 = timelines[i];
      const timeline2 = timelines[j];
      
      // Find equity crossover
      for (let month = 1; month < timeline1.points.length; month++) {
        const prev1 = timeline1.points[month - 1].equity;
        const curr1 = timeline1.points[month].equity;
        const prev2 = timeline2.points[month - 1].equity;
        const curr2 = timeline2.points[month].equity;
        
        // Check if lines crossed
        if ((prev1 < prev2 && curr1 >= curr2) || (prev1 > prev2 && curr1 <= curr2)) {
          crossoverPoints.push({
            month,
            scenario1: timeline1.scenarioId,
            scenario2: timeline2.scenarioId,
            metric: 'equity',
            value: curr1,
            description: `${timeline1.scenarioName} and ${timeline2.scenarioName} have equal equity`
          });
        }
      }
      
      // Find cost crossover
      for (let month = 1; month < timeline1.points.length; month++) {
        const prev1 = timeline1.points[month - 1].totalPaid;
        const curr1 = timeline1.points[month].totalPaid;
        const prev2 = timeline2.points[month - 1].totalPaid;
        const curr2 = timeline2.points[month].totalPaid;
        
        if ((prev1 < prev2 && curr1 >= curr2) || (prev1 > prev2 && curr1 <= curr2)) {
          crossoverPoints.push({
            month,
            scenario1: timeline1.scenarioId,
            scenario2: timeline2.scenarioId,
            metric: 'cost',
            value: curr1,
            description: `${timeline1.scenarioName} and ${timeline2.scenarioName} have equal total paid`
          });
        }
      }
    }
  }
  
  return crossoverPoints;
};
